import { useEffect, useMemo, useState } from 'react';
import { userAppContext } from '@/context/Context';
import { UserContext } from '@/types';
import { fetchFeatureSubscriptions } from './featureBillingService';
import { FeatureSubscription } from './types';

export function getGseaEntitlementFromSubscriptions(subscriptions: FeatureSubscription[] | undefined) {
  const list = Array.isArray(subscriptions) ? subscriptions : [];
  const hasGseaAccess = list.length > 0;
  const hasGseaProPlan = list.some(sub => sub.tier === 'pro');
  const hasGseaPro = list.some(sub => sub.tier === 'pro' && sub.status !== 'trialing');

  return { hasGseaAccess, hasGseaPro, hasGseaProPlan };
}

export function useGseaEntitlement() {
  const { user, isAuthenticated } = userAppContext() as UserContext;
  const email = user?.email as string | undefined;
  const [subscriptions, setSubscriptions] = useState<FeatureSubscription[]>([]);
  const [loading, setLoading] = useState(Boolean(isAuthenticated && email));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !email) {
      setSubscriptions([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    fetchFeatureSubscriptions(email)
      .then(list => {
        if (cancelled) return;
        setSubscriptions(list);
        setError(null);
      })
      .catch(err => {
        if (cancelled) return;
        setSubscriptions([]);
        setError(err instanceof Error ? err.message : 'Failed to load GSEA subscription');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [email, isAuthenticated]);

  const entitlement = useMemo(() => getGseaEntitlementFromSubscriptions(subscriptions), [subscriptions]);

  return { ...entitlement, loading, error, subscriptions };
}
